import type { Feature, FeatureCollection } from 'geojson';
import maplibregl, { type GeoJSONSource } from 'maplibre-gl';
import { useEffect, useRef } from 'react';
import { useMap } from './MapView';
import { circlePolygon } from './PlacesLayer';

export type DrawState =
  | { shape: 'CIRCLE'; center: [number, number] | null; radiusM: number }
  | { shape: 'POLYGON'; points: [number, number][] };

const SRC = 'draw';

/**
 * Minimal drawing tool: CIRCLE = click to place the center (drag marker to move),
 * POLYGON = click to add vertices, double-click / "finish" to close.
 */
export function DrawPlace({ state, onChange }: { state: DrawState; onChange: (s: DrawState) => void }) {
  const map = useMap();
  const marker = useRef<maplibregl.Marker | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Render preview geometry
  useEffect(() => {
    if (!map) return;
    const s = state;
    const features: Feature[] = [];
    if (s.shape === 'CIRCLE' && s.center) {
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [circlePolygon(s.center[0], s.center[1], s.radiusM)] } });
    } else if (s.shape === 'POLYGON' && s.points.length) {
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: s.points } });
      if (s.points.length >= 3) features.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[...s.points, s.points[0]!]] } });
      for (const p of s.points) features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: p } });
    }
    const data: FeatureCollection = { type: 'FeatureCollection', features };
    const src = map.getSource(SRC) as GeoJSONSource | undefined;
    if (src) src.setData(data);
    else {
      map.addSource(SRC, { type: 'geojson', data });
      map.addLayer({ id: `${SRC}-fill`, type: 'fill', source: SRC, filter: ['==', '$type', 'Polygon'], paint: { 'fill-color': '#2563EB', 'fill-opacity': 0.2 } });
      map.addLayer({ id: `${SRC}-line`, type: 'line', source: SRC, filter: ['in', '$type', 'Polygon', 'LineString'], paint: { 'line-color': '#2563EB', 'line-width': 2, 'line-dasharray': [2, 1] } });
      map.addLayer({ id: `${SRC}-pt`, type: 'circle', source: SRC, filter: ['==', '$type', 'Point'], paint: { 'circle-radius': 5, 'circle-color': '#fff', 'circle-stroke-color': '#2563EB', 'circle-stroke-width': 2 } });
    }
    // Center marker for circles (draggable)
    if (s.shape === 'CIRCLE' && s.center) {
      if (!marker.current) {
        marker.current = new maplibregl.Marker({ draggable: true, color: '#2563EB' }).setLngLat(s.center).addTo(map);
        marker.current.on('dragend', () => {
          const ll = marker.current!.getLngLat();
          const cur = stateRef.current;
          if (cur.shape === 'CIRCLE') onChange({ ...cur, center: [ll.lng, ll.lat] });
        });
      } else marker.current.setLngLat(s.center);
    } else if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }
  }, [map, state, onChange]);

  // Click handling
  useEffect(() => {
    if (!map) return;
    map.getCanvas().style.cursor = 'crosshair';
    const onClick = (e: maplibregl.MapMouseEvent) => {
      const cur = stateRef.current;
      const p: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      if (cur.shape === 'CIRCLE') onChange({ ...cur, center: p });
      else onChange({ ...cur, points: [...cur.points, p] });
    };
    map.on('click', onClick);
    map.doubleClickZoom.disable();
    return () => {
      map.off('click', onClick);
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = '';
      marker.current?.remove();
      marker.current = null;
      if (map.getLayer(`${SRC}-fill`)) {
        map.removeLayer(`${SRC}-fill`);
        map.removeLayer(`${SRC}-line`);
        map.removeLayer(`${SRC}-pt`);
        map.removeSource(SRC);
      }
    };
  }, [map, onChange]);

  return null;
}
